import { computeMasterDigest, normalizeDealConfig } from "./config";
import { bytesToHex, compareUtf8Bytes, getSubtleCrypto } from "./encoding";
import { fail } from "./errors";
import { computeDealFingerprint } from "./fingerprint";
import {
  DOMAIN_TAG,
  Sha256DomainStream,
  fisherYates,
  uniformIndex
} from "./random";
import type {
  DealCatalog,
  DealConfigInput,
  DealLineupDefinition,
  DealResult,
  DealThemeDefinition,
  SeatDeal
} from "./types";
import { assertValidCatalog } from "./validation";

function compareThemes(
  left: DealThemeDefinition,
  right: DealThemeDefinition
): number {
  return (
    compareUtf8Bytes(left.themeId, right.themeId) ||
    compareUtf8Bytes(left.themeRevision, right.themeRevision)
  );
}

function compareLineups(
  left: DealLineupDefinition,
  right: DealLineupDefinition
): number {
  return compareUtf8Bytes(left.lineupId, right.lineupId);
}

export async function createDeal(
  input: DealConfigInput,
  catalog: DealCatalog,
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<DealResult> {
  const config = normalizeDealConfig(input);
  if (catalog.catalogRevision !== config.catalogRevision) {
    fail(
      "CATALOG_REVISION_NOT_FOUND",
      `Expected ${config.catalogRevision}, received ${catalog.catalogRevision}`
    );
  }
  assertValidCatalog(catalog);

  const masterDigest = await computeMasterDigest(config, subtle);
  const candidateThemes = catalog.themes
    .filter(
      (theme) =>
        theme.compatibleRulesVersion === config.rulesVersion &&
        theme.compatibleDealVersion === config.dealVersion &&
        theme.compatibleMatcherVersion === config.matcherVersion &&
        theme.supportedPlayerCounts.includes(config.playerCount) &&
        (theme.allowedLineups[config.playerCount]?.length ?? 0) > 0
    )
    .sort(compareThemes);
  if (candidateThemes.length === 0) {
    fail("NO_COMPATIBLE_THEME", "Catalog contains no compatible theme");
  }

  const themeStream = new Sha256DomainStream(
    masterDigest,
    DOMAIN_TAG.THEME,
    [],
    subtle
  );
  const themeIndex = await uniformIndex(themeStream, candidateThemes.length);
  const theme = candidateThemes[themeIndex]!;

  const lineups = [...(theme.allowedLineups[config.playerCount] ?? [])].sort(
    compareLineups
  );
  if (lineups.length === 0) {
    fail("NO_LINEUP", "Selected theme contains no lineup for playerCount");
  }
  const lineupStream = new Sha256DomainStream(
    masterDigest,
    DOMAIN_TAG.LINEUP,
    [theme.themeId, theme.themeRevision],
    subtle
  );
  const lineupIndex = await uniformIndex(lineupStream, lineups.length);
  const lineup = lineups[lineupIndex]!;

  const seatStream = new Sha256DomainStream(
    masterDigest,
    DOMAIN_TAG.SEAT_PERMUTATION,
    [theme.themeId, theme.themeRevision, lineup.lineupId],
    subtle
  );
  const permutation = await fisherYates(lineup.packetIds, seatStream);
  const packetById = new Map(
    theme.packets.map((packet) => [packet.packetId, packet] as const)
  );
  const deals: SeatDeal[] = permutation.values.map((packetId, index) => {
    const packet = packetById.get(packetId);
    if (packet === undefined) {
      fail("INVALID_CATALOG", `Unknown selected packet: ${packetId}`);
    }
    return {
      seat: index + 1,
      packetId,
      initial: [...packet.initial],
      penalty: [...packet.penalty]
    };
  });

  const seatToPacketMapping: Record<number, string> = {};
  for (const deal of deals) {
    seatToPacketMapping[deal.seat] = deal.packetId;
  }
  const dealFingerprint = await computeDealFingerprint(
    config,
    theme.themeId,
    theme.themeRevision,
    lineup.lineupId,
    deals,
    subtle
  );

  return {
    config,
    themeId: theme.themeId,
    themeRevision: theme.themeRevision,
    lineupId: lineup.lineupId,
    selectedPacketIds: [...lineup.packetIds],
    seatToPacketMapping,
    deals,
    dealFingerprint,
    diagnostics: {
      masterDigestHex: bytesToHex(masterDigest),
      themeIndex,
      lineupIndex,
      fisherYatesJ: permutation.swaps
    }
  };
}
